import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';

import {
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-row-details-modal',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <div class="modal-container">
      <h2>Detaljer</h2>
      <div class="modal-content">
        @for (field of fields; track field.label) {
        <div class="field-row">
          <span class="field-label">{{ field.label }}</span>
          <span class="field-value">{{ field.value }}</span>
        </div>
        }
      </div>
      <div class="modal-actions">
        @if (actionButton) {
        <button mat-button (click)="onActionClick()">
          {{ actionButton.text }}
        </button>
        }
        <button mat-button (click)="close()">Luk</button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .modal-container {
        padding: 16px 20px 12px;
        max-height: min(85vh, 720px);
        display: flex;
        flex-direction: column;
      }

      h2 {
        margin: 0 0 16px 0;
        font-size: 1.5rem;
        flex-shrink: 0;
      }

      .modal-content {
        margin-bottom: 16px;
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
        padding-right: 4px;
      }

      .field-row {
        margin-bottom: 14px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .field-label {
        font-weight: 600;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(0, 0, 0, 0.55);
      }

      .field-value {
        color: rgba(0, 0, 0, 0.87);
        white-space: pre-wrap;
        word-break: break-word;
        line-height: 1.45;
        font-size: 0.95rem;
      }

      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        flex-shrink: 0;
        padding-top: 8px;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
      }
    `,
  ],
})
export class RowDetailsModalComponent {
  fields: { label: string; value: string }[] = [];
  actionButton?: { text: string; action: () => void };

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<RowDetailsModalComponent>
  ) {
    this.processData();
  }

  private processData() {
    if (!this.data) {
      return;
    }

    const { row, columns, action, extraFields } = this.data;

    this.fields = (columns ?? [])
      .filter((col: any) => col.type !== 'button' && col.header)
      .map((col: any) => ({
        label: col.header,
        value: this.formatColumnValue(col, row),
      }))
      .filter((field: { label: string; value: string }) => this.isMeaningful(field.value));

    if (Array.isArray(extraFields)) {
      for (const extra of extraFields) {
        const value = extra.value(row);
        if (this.isMeaningful(value)) {
          this.fields.push({ label: extra.label, value: String(value) });
        }
      }
    }

    const buttonColumn = columns?.find((col: any) => col.type === 'button');
    if (buttonColumn && action && !buttonColumn.opensRowDetailsModal) {
      this.actionButton = {
        text: buttonColumn.buttonText,
        action: () => action(row),
      };
    }
  }

  private formatColumnValue(col: any, row: any): string {
    const raw = row[col.key];
    if (col.detailTransform) {
      return String(col.detailTransform(raw, row) ?? '');
    }
    if (col.transform) {
      return String(col.transform(raw, row) ?? '');
    }
    if (col.format) {
      return String(col.format(raw) ?? '');
    }
    if (raw === null || raw === undefined || raw === '') {
      return '';
    }
    return String(raw);
  }

  private isMeaningful(value: string | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    const trimmed = String(value).trim();
    return trimmed !== '' && trimmed !== '-';
  }

  onActionClick() {
    if (this.actionButton) {
      this.actionButton.action();
      this.dialogRef.close();
    }
  }

  close() {
    this.dialogRef.close();
  }
}
